/**
 * @file path-parser.ts
 * @description SVG path string ↔ BezierNode[] conversion utilities.
 *
 * Phase 1 supports: M/m, L/l, C/c, S/s, Q/q, Z/z
 * Phase 2 will add: A/a, H/h, V/v, T/t
 *
 * All relative commands are converted to absolute.
 * All segments are normalised to cubic beziers internally.
 */

import type { BezierNode, PathData, Point2D } from './path-types';

// ─── SVG tokeniser ────────────────────────────────────────────────────────────

/** Splits an SVG path "d" attribute into command + numeric-arg tokens. */
function tokenise(d: string): Array<{ cmd: string; args: number[] }> {
    const result: Array<{ cmd: string; args: number[] }> = [];
    // Match command letters and sequences of numbers (including negatives/decimals)
    const re = /([MmLlCcSsQqZzHhVvTtAa])([^MmLlCcSsQqZzHhVvTtAa]*)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(d)) !== null) {
        const cmd = m[1];
        const raw = m[2].trim();
        const args: number[] = raw.length
            ? raw.split(/[\s,]+|(?=-)/).filter(Boolean).map(Number)
            : [];
        result.push({ cmd, args });
    }
    return result;
}

// ─── Numeric helpers ──────────────────────────────────────────────────────────

function uid(): string {
    return Math.random().toString(36).slice(2, 9);
}

function makeNode(x: number, y: number): BezierNode {
    return { id: uid(), x, y, nodeType: 'corner' };
}

function makeNodeWithHandles(
    x: number, y: number,
    cp1x: number, cp1y: number,
    cp2x: number, cp2y: number
): BezierNode {
    return {
        id: uid(), x, y, nodeType: 'smooth',
        cp1: { x: cp1x, y: cp1y },
        cp2: { x: cp2x, y: cp2y },
    };
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Parses an SVG path string into an editable BezierNode array.
 * Relative commands are converted to absolute. Quadratic beziers are promoted to cubic.
 */
export function parseSvgPath(svgPath: string): PathData {
    const tokens = tokenise(svgPath.trim());
    const nodes: BezierNode[] = [];
    let isClosed = false;

    let cx = 0; // current point
    let cy = 0;
    let lastCp2x = 0; // reflection of last cp2 for S/s
    let lastCp2y = 0;
    let lastQCpx = 0; // reflection of last quadratic cp for T/t
    let lastQCpy = 0;
    let lastCmd = '';

    for (const { cmd, args } of tokens) {
        const isRelative = cmd === cmd.toLowerCase() && cmd !== 'z' && cmd !== 'Z';
        const base = cmd.toUpperCase();

        switch (base) {
            case 'M': {
                // M x y [L x y ...] — implicit L after first pair
                for (let i = 0; i < args.length; i += 2) {
                    let x = args[i];
                    let y = args[i + 1];
                    if (isRelative) { x += cx; y += cy; }
                    cx = x; cy = y;
                    nodes.push(makeNode(x, y));
                }
                break;
            }
            case 'L': {
                for (let i = 0; i < args.length; i += 2) {
                    let x = args[i];
                    let y = args[i + 1];
                    if (isRelative) { x += cx; y += cy; }
                    cx = x; cy = y;
                    nodes.push(makeNode(x, y));
                }
                break;
            }
            case 'H': {
                for (let i = 0; i < args.length; i++) {
                    let x = args[i];
                    if (isRelative) x += cx;
                    cx = x;
                    nodes.push(makeNode(x, cy));
                }
                break;
            }
            case 'V': {
                for (let i = 0; i < args.length; i++) {
                    let y = args[i];
                    if (isRelative) y += cy;
                    cy = y;
                    nodes.push(makeNode(cx, y));
                }
                break;
            }
            case 'C': {
                // C cp1x cp1y cp2x cp2y x y [...]
                for (let i = 0; i < args.length; i += 6) {
                    let [cp1x, cp1y, cp2x, cp2y, x, y] = args.slice(i, i + 6);
                    if (isRelative) {
                        cp1x += cx; cp1y += cy;
                        cp2x += cx; cp2y += cy;
                        x    += cx; y    += cy;
                    }
                    // Attach cp2 to the previous node
                    if (nodes.length > 0) {
                        nodes[nodes.length - 1].cp2 = { x: cp1x, y: cp1y };
                    }
                    nodes.push(makeNodeWithHandles(x, y, cp2x, cp2y, x, y));
                    // cp1 for next segment is stored as cp1 of the new node
                    nodes[nodes.length - 1].cp1 = { x: cp2x, y: cp2y };
                    lastCp2x = cp2x; lastCp2y = cp2y;
                    cx = x; cy = y;
                }
                break;
            }
            case 'S': {
                // S cp2x cp2y x y — reflects last cp2
                for (let i = 0; i < args.length; i += 4) {
                    let [cp2x, cp2y, x, y] = args.slice(i, i + 4);
                    if (isRelative) {
                        cp2x += cx; cp2y += cy;
                        x    += cx; y    += cy;
                    }
                    // Reflected cp1
                    const cp1x = 2 * cx - lastCp2x;
                    const cp1y = 2 * cy - lastCp2y;
                    if (nodes.length > 0) {
                        nodes[nodes.length - 1].cp2 = { x: cp1x, y: cp1y };
                    }
                    nodes.push(makeNodeWithHandles(x, y, cp2x, cp2y, x, y));
                    nodes[nodes.length - 1].cp1 = { x: cp2x, y: cp2y };
                    lastCp2x = cp2x; lastCp2y = cp2y;
                    cx = x; cy = y;
                }
                break;
            }
            case 'Q': {
                // Q cpx cpy x y — promote quadratic to cubic
                for (let i = 0; i < args.length; i += 4) {
                    let [qcpx, qcpy, x, y] = args.slice(i, i + 4);
                    if (isRelative) {
                        qcpx += cx; qcpy += cy;
                        x    += cx; y    += cy;
                    }
                    // Cubic approximation: cp1 = start + 2/3 * (qcp - start), cp2 = end + 2/3 * (qcp - end)
                    const cp1x = cx + 2/3 * (qcpx - cx);
                    const cp1y = cy + 2/3 * (qcpy - cy);
                    const cp2x = x  + 2/3 * (qcpx - x);
                    const cp2y = y  + 2/3 * (qcpy - y);
                    if (nodes.length > 0) {
                        nodes[nodes.length - 1].cp2 = { x: cp1x, y: cp1y };
                    }
                    nodes.push(makeNodeWithHandles(x, y, cp2x, cp2y, x, y));
                    nodes[nodes.length - 1].cp1 = { x: cp2x, y: cp2y };
                    lastQCpx = qcpx; lastQCpy = qcpy;
                    cx = x; cy = y;
                }
                break;
            }
            case 'T': {
                // T x y — smooth quadratic (reflects previous Q cp)
                for (let i = 0; i < args.length; i += 2) {
                    let x = args[i], y = args[i + 1];
                    if (isRelative) { x += cx; y += cy; }
                    const qcpx = 2 * cx - lastQCpx;
                    const qcpy = 2 * cy - lastQCpy;
                    const cp1x = cx + 2/3 * (qcpx - cx);
                    const cp1y = cy + 2/3 * (qcpy - cy);
                    const cp2x = x  + 2/3 * (qcpx - x);
                    const cp2y = y  + 2/3 * (qcpy - y);
                    if (nodes.length > 0) {
                        nodes[nodes.length - 1].cp2 = { x: cp1x, y: cp1y };
                    }
                    nodes.push(makeNodeWithHandles(x, y, cp2x, cp2y, x, y));
                    nodes[nodes.length - 1].cp1 = { x: cp2x, y: cp2y };
                    lastQCpx = qcpx; lastQCpy = qcpy;
                    cx = x; cy = y;
                }
                break;
            }
            case 'Z': {
                isClosed = true;
                break;
            }
            // A (arc) deferred to Phase 2 — skip the 7 args per arc segment
            default:
                break;
        }

        if (base !== 'S' && base !== 'C') { lastCp2x = cx; lastCp2y = cy; }
        if (base !== 'Q' && base !== 'T') { lastQCpx = cx; lastQCpy = cy; }
        lastCmd = base;
    }

    return { nodes, isClosed };
}

/**
 * Serialises a PathData back to an SVG path string.
 * Uses absolute C commands for all curved segments, L for straight lines.
 */
export function serializePath(pathData: PathData): string {
    const { nodes, isClosed } = pathData;
    if (nodes.length === 0) return '';

    const parts: string[] = [];
    const fmt = (n: number) => +n.toFixed(3);

    parts.push(`M ${fmt(nodes[0].x)} ${fmt(nodes[0].y)}`);

    for (let i = 1; i < nodes.length; i++) {
        const prev = nodes[i - 1];
        const curr = nodes[i];
        const cp1 = prev.cp2 ?? prev;       // out-handle of previous anchor
        const cp2 = curr.cp1 ?? curr;       // in-handle of current anchor

        if (!prev.cp2 && !curr.cp1) {
            // Both handles missing → straight line
            parts.push(`L ${fmt(curr.x)} ${fmt(curr.y)}`);
        } else {
            parts.push(
                `C ${fmt(cp1.x)} ${fmt(cp1.y)} ` +
                `${fmt(cp2.x)} ${fmt(cp2.y)} ` +
                `${fmt(curr.x)} ${fmt(curr.y)}`
            );
        }
    }

    if (isClosed && nodes.length > 1) {
        // Closing segment from last back to first
        const prev = nodes[nodes.length - 1];
        const curr = nodes[0];
        const cp1 = prev.cp2 ?? prev;
        const cp2 = curr.cp1 ?? curr;
        if (prev.cp2 || curr.cp1) {
            parts.push(
                `C ${fmt(cp1.x)} ${fmt(cp1.y)} ` +
                `${fmt(cp2.x)} ${fmt(cp2.y)} ` +
                `${fmt(curr.x)} ${fmt(curr.y)}`
            );
        }
        parts.push('Z');
    }

    return parts.join(' ');
}

/**
 * Evaluates a point on a cubic bezier at parameter t ∈ [0, 1].
 */
export function cubicBezierPoint(
    p0: Point2D, cp1: Point2D, cp2: Point2D, p1: Point2D,
    t: number
): Point2D {
    const mt = 1 - t;
    const mt2 = mt * mt;
    const t2 = t * t;
    return {
        x: mt2 * mt * p0.x + 3 * mt2 * t * cp1.x + 3 * mt * t2 * cp2.x + t2 * t * p1.x,
        y: mt2 * mt * p0.y + 3 * mt2 * t * cp1.y + 3 * mt * t2 * cp2.y + t2 * t * p1.y,
    };
}

/**
 * Finds the closest t parameter on a cubic bezier to a given test point.
 * Uses 32-sample Newton-like bisection for reasonable accuracy.
 */
export function findClosestT(
    p0: Point2D, cp1: Point2D, cp2: Point2D, p1: Point2D,
    testPoint: Point2D
): { t: number; distance: number } {
    const SAMPLES = 32;
    let bestT = 0;
    let bestDist = Infinity;

    for (let i = 0; i <= SAMPLES; i++) {
        const t = i / SAMPLES;
        const pt = cubicBezierPoint(p0, cp1, cp2, p1, t);
        const dx = pt.x - testPoint.x;
        const dy = pt.y - testPoint.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < bestDist) {
            bestDist = dist;
            bestT = t;
        }
    }

    // Refine with 2 iterations of bisection around bestT
    let lo = Math.max(0, bestT - 1 / SAMPLES);
    let hi = Math.min(1, bestT + 1 / SAMPLES);
    for (let iter = 0; iter < 8; iter++) {
        const mid = (lo + hi) / 2;
        const ptLo = cubicBezierPoint(p0, cp1, cp2, p1, lo);
        const ptHi = cubicBezierPoint(p0, cp1, cp2, p1, hi);
        const dLo = (ptLo.x - testPoint.x) ** 2 + (ptLo.y - testPoint.y) ** 2;
        const dHi = (ptHi.x - testPoint.x) ** 2 + (ptHi.y - testPoint.y) ** 2;
        if (dLo < dHi) hi = mid; else lo = mid;
    }

    const tFinal = (lo + hi) / 2;
    const ptFinal = cubicBezierPoint(p0, cp1, cp2, p1, tFinal);
    const dxF = ptFinal.x - testPoint.x;
    const dyF = ptFinal.y - testPoint.y;
    return { t: tFinal, distance: Math.sqrt(dxF * dxF + dyF * dyF) };
}
